/** Dates in Hestia are wall-clock, not instants.
 *  A task happens on a calendar day in the household's timezone, at an optional
 *  local time. Storing `YYYY-MM-DD` + `HH:MM` instead of a UTC timestamp removes
 *  every DST and offset bug from recurrence — the price is that we cannot express
 *  "18:00 in another timezone", which a family todo app never needs. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const isIsoDate = (v) => DATE_RE.test(v) && !Number.isNaN(Date.parse(v));
export const isLocalTime = (v) => TIME_RE.test(v);
/** Today in the given IANA timezone, as YYYY-MM-DD. */
export function todayIn(tz, now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(now);
}
/** Current local time in the given timezone, as HH:MM. */
export function timeNowIn(tz, now = new Date()) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(now);
}
/** Parse YYYY-MM-DD into a UTC-midnight Date. Used only as a calendar anchor —
 *  never as an instant in time. */
export function dateToUtc(d) {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, day));
}
export function utcToDate(d) {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
export function addDays(d, n) {
    const x = dateToUtc(d);
    x.setUTCDate(x.getUTCDate() + n);
    return utcToDate(x);
}
/** 0 = Monday … 6 = Sunday. Monday-first, because the calendar is. */
export function weekdayIndex(d) {
    return (dateToUtc(d).getUTCDay() + 6) % 7;
}
export function compareDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Inclusive range of dates. */
export function eachDay(from, to) {
    const out = [];
    for (let d = from; d <= to; d = addDays(d, 1))
        out.push(d);
    return out;
}
export function startOfMonth(d) { return `${d.slice(0, 7)}-01`; }
export function endOfMonth(d) {
    const [y, m] = d.split('-').map(Number);
    return utcToDate(new Date(Date.UTC(y, m, 0)));
}
/** The Monday on or before `d`. */
export function startOfWeek(d) { return addDays(d, -weekdayIndex(d)); }
/** The days a month grid actually draws — including the leading and trailing days
 *  that belong to the neighbouring months. Always whole weeks, so the grid is a
 *  rectangle. Shared, because two things must agree on it exactly: the calendar
 *  fetches this range (or the edge days render empty when they are not), and the
 *  date picker draws it. */
export function monthGrid(month) {
    const last = endOfMonth(month);
    const days = [];
    for (let d = startOfWeek(startOfMonth(month)); d <= last || weekdayIndex(d) !== 0; d = addDays(d, 1)) {
        days.push(d);
        if (days.length >= 42)
            break;
    }
    while (days.length % 7)
        days.push(addDays(days[days.length - 1], 1));
    return days;
}
/** The same day, `n` months away — clamped when the target month is shorter, so
 *  31 January plus a month is 28 February and not 3 March. Used by the date
 *  picker's month and year steps, where landing on "the same day next month" is
 *  what a person means and what the ARIA pattern asks for. */
export function addMonths(d, n) {
    const [y, m, day] = d.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1 + n, 1));
    const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
    first.setUTCDate(Math.min(day, lastDay));
    return first.toISOString().slice(0, 10);
}
//# sourceMappingURL=date.js.map